"use client";

import { createApi } from "@reduxjs/toolkit/query/react";
import qs from "qs";

import baseQuery from "@/libs/shared/api/baseQuery";
import { objectToFormData } from "@/libs/shared/api/objectToFormData";
import { GetPayloadModel, GetResponseModel } from "@/libs/shared/api/api.types";
import { TagProps } from "@/libs/tags/redux/tags.types";

const tagTypes = ["Tags" as const];

export const invalidateTags = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(tagsApi.util.invalidateTags([tag as any]));
      });
    });
  };
};

export const tagsApi = createApi({
  reducerPath: "tagsApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getTags: builder.query<
      GetResponseModel<TagProps>,
      Partial<GetPayloadModel> & { type?: string; base?: number }
    >({
      query: ({
        perPage,
        page,
        name,
        orderBy,
        orderByDirection,
        type,
      }: Partial<GetPayloadModel> & { type?: string; base?: number }) => {
        const queryString = qs.stringify({
          per_page: perPage,
          page,
          name,
          order_by: orderBy,
          order_by_direction: orderByDirection,
          type,
        });
        return {
          url: `/pt/tags?${queryString}`,
        };
      },
      providesTags: ["Tags"],
    }),
    createTag: builder.mutation({
      query: (tag: { name: string; type: string }) => {
        const formData = objectToFormData(tag);
        return {
          url: "/pt/tags",
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateTags(["Tags"]),
    }),
    editTag: builder.mutation({
      query: (tag: { id: number; name: string }) => {
        return {
          url: `/pt/tags`,
          method: "PUT",
          body: {
            id: tag.id.toString(),
            name: tag.name,
          },
        };
      },
      onQueryStarted: invalidateTags(["Tags"]),
    }),
    deleteTag: builder.mutation({
      query: (ids: string) => {
        return {
          url: `/pt/tags?id=${ids}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateTags(["Tags"]),
    }),
  }),
});

export const {
  useGetTagsQuery,
  useCreateTagMutation,
  useDeleteTagMutation,
  useEditTagMutation,
} = tagsApi;
